import { z } from 'zod';

import type { Database } from '../../lib/database.types';

export const QUOTE_STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'rejected', 'expired'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABELS_HE: Record<QuoteStatus, string> = {
  draft: 'טיוטה',
  sent: 'נשלחה',
  viewed: 'נצפתה',
  accepted: 'אושרה',
  rejected: 'נדחתה',
  expired: 'פגה',
};

export const lineItemSchema = z.object({
  service_label: z.string().trim().min(1, 'שדה חובה').max(120, 'עד 120 תווים'),
  description: z.string().trim().max(500, 'עד 500 תווים').optional(),
  quantity: z.number().min(0).max(9999),
  unit_price: z.number().min(0).max(9999999),
});

export type LineItemInput = z.infer<typeof lineItemSchema>;

export const quoteSchema = z.object({
  customer_id: z.string().uuid().nullable().optional(),
  primary_contact_id: z.string().uuid().nullable().optional(),
  source_lead_id: z.string().uuid().nullable().optional(),
  valid_until: z.string().nullable().optional(),
  notes: z.string().trim().max(2000).optional(),
  status: z.enum(QUOTE_STATUSES).optional(),
  items: z.array(lineItemSchema).min(1, 'נדרשת שורה אחת לפחות'),
});

export type QuoteInput = z.infer<typeof quoteSchema>;

type QuoteTableRow = Database['public']['Tables']['quotes']['Row'];
type LineItemTableRow = Database['public']['Tables']['quote_line_items']['Row'];

export type QuoteRow = Pick<
  QuoteTableRow,
  | 'id'
  | 'quote_number'
  | 'status'
  | 'customer_id'
  | 'primary_contact_id'
  | 'source_lead_id'
  | 'value_amount'
  | 'value_currency'
  | 'valid_until'
  | 'sent_at'
  | 'viewed_at'
  | 'accepted_at'
  | 'rejected_at'
  | 'notes'
  | 'created_at'
  | 'updated_at'
>;

export type LineItemRow = Pick<
  LineItemTableRow,
  | 'id'
  | 'quote_id'
  | 'service_label'
  | 'description'
  | 'quantity'
  | 'unit_price'
  | 'subtotal'
  | 'sort_order'
  | 'created_at'
>;

export interface CustomerOption {
  id: string;
  name: string;
}

export interface LeadOption {
  id: string;
  full_name: string;
  company_name: string | null;
}

export function computeTotal(items: { quantity: number; unit_price: number }[]): number {
  return items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
}

/**
 * Format an ILS amount as `₪ 24,500` (brand format: symbol + single space + grouped integer).
 * Intl's he-IL ILS produces `‏₪24,500.00` without the brand space; we post-process.
 */
export function formatILS(amount: number): string {
  const number = new Intl.NumberFormat('he-IL', {
    maximumFractionDigits: 0,
  }).format(amount);
  return `₪ ${number}`;
}

export function buildShareUrl(quoteId: string): string {
  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : 'https://app.focusmode.info';
  return `${origin}/q/${quoteId}`;
}

const QUOTE_BADGE_CLASS: Record<QuoteStatus, string> = {
  draft: 'badge--draft',
  sent: 'badge--sent',
  viewed: 'badge--viewed',
  accepted: 'badge--accepted',
  rejected: 'badge--rejected',
  expired: 'badge--expired',
};

/** Returns the badge variant class name for a quote status, or '' if unknown. */
export function statusBadgeClass(status: string): string {
  return (QUOTE_BADGE_CLASS as Record<string, string>)[status] ?? '';
}

